import {
  ComposeEmailPaneButton,
  DataCatalogApolloClient,
  getAllFromSliceable,
  GraphQLSliceable,
  MultiSelectionProvider,
  PaneTitle,
  Sliceable,
  VerticalFlexContainer
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import {
  DatabaseDownstreamOwnerIdsFilteredByTableQuery,
  DatabaseDownstreamOwnerIdsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamOwnerIdsFilteredByTableQuery';
import {
  DatabaseDownstreamOwnerIdsQuery,
  DatabaseDownstreamOwnerIdsQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamOwnerIdsQuery';
import { OwnerIdFragment } from '../../../gen-src/queries/OwnerIdFragment';
import {
  DatabaseDownstreamOwnerIdsFilteredByTableQueryGql,
} from '../../queries/DatabaseDownstreamOwnerIdsFilteredByTableQueryGql';
import { DatabaseDownstreamOwnerIdsQueryGql } from '../../queries/DatabaseDownstreamOwnerIdsQueryGql';
import { OwnersGridSortProvider } from '../sorting/sortProviders/OwnersGridSortProvider';
import { MultiSelectionControls } from '../widgets/MultiSelectionControls';
import { DatabaseDownstreamOwnersQueryGrid } from './DatabaseDownstreamOwnersQueryGrid';

export type DatabaseDownstreamOwnersPaneProps = {
  readonly title: string;
  readonly databaseVizportalId: string;
  readonly databaseId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export class DatabaseDownstreamOwnersPane extends React.Component<DatabaseDownstreamOwnersPaneProps> {

  public render(): JSX.Element {
    return (
      <MultiSelectionProvider selectionId={this.props.databaseId}>
        {({ selectedKeys, setSelectedKeys, updateSelectionForKey }) =>
          <VerticalFlexContainer fillFlexParent={true}>
            <PaneTitle>
              {this.props.title}
              {this.props.rowCount != null && this.props.rowCount > 0 ?
                <>
                  <MultiSelectionControls
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    updateSelectionForKey={updateSelectionForKey}
                    fetchAllKeys={this.fetchAllOwnerKeys}
                  />
                  <ComposeEmailPaneButton
                    selectedUserIds={selectedKeys}
                    contentType='database'
                    contentId={this.props.databaseVizportalId}
                  />
                </>
                : null
              }
            </PaneTitle>
            <OwnersGridSortProvider>
              {({ orderBy, updateSortOrder }) =>
                <DatabaseDownstreamOwnersQueryGrid
                  databaseId={this.props.databaseId}
                  selectedKeys={selectedKeys}
                  updateSelectionForKey={updateSelectionForKey}
                  filteredTableId={this.props.filteredTableId}
                  rowCount={this.props.rowCount}
                  orderBy={orderBy}
                  updateSortOrder={updateSortOrder}
                />
              }
            </OwnersGridSortProvider>
          </VerticalFlexContainer>
        }
      </MultiSelectionProvider>
    );
  }

  private readonly fetchAllOwnerKeys = async (client: DataCatalogApolloClient): Promise<ReadonlyArray<string>> => {
    const sliceable = this.createOwnerIdsSliceable(client);
    const items = await getAllFromSliceable(sliceable);
    return items.map(item => item.vizportalId);
  }

  private createOwnerIdsSliceable(client: DataCatalogApolloClient): Sliceable<OwnerIdFragment> {
    if (this.props.filteredTableId) {
      return new GraphQLSliceable<
        OwnerIdFragment,
        OwnerIdFragment,
        DatabaseDownstreamOwnerIdsFilteredByTableQueryVariables,
        DatabaseDownstreamOwnerIdsFilteredByTableQuery
      >({
        queryGql: DatabaseDownstreamOwnerIdsFilteredByTableQueryGql,
        selectConnectionListResult: (resultData) => {
          const table = resultData.tables && resultData.tables[0];
          return table.downstreamOwnersConnection;
        },
        mapResultNode: (node) => node,
      }, { tableId: this.props.filteredTableId }, client);
    } else {
      return new GraphQLSliceable<
        OwnerIdFragment,
        OwnerIdFragment,
        DatabaseDownstreamOwnerIdsQueryVariables,
        DatabaseDownstreamOwnerIdsQuery
      >({
        queryGql: DatabaseDownstreamOwnerIdsQueryGql,
        selectConnectionListResult: (resultData) => {
          const database = resultData.databases && resultData.databases[0];
          return database.downstreamOwnersConnection;
        },
        mapResultNode: (node) => node,
      }, { databaseId: this.props.databaseId }, client);
    }
  }

}
